import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import TimePicker24 from "./TimePicker24";
import SearchableSelect from "./SearchableSelect";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Trash2, CalendarPlus, Repeat } from "lucide-react";

/**
 * Modale de gestion des plages horaires réservées de l'Agenda départements.
 *
 * Une plage n'est PAS une intervention — c'est un guide visuel pour le
 * secrétariat (ex. « Mardi 8h-12h = créneaux réservés aux RDV Consulting »).
 *
 * Récurrence flexible :
 *   - Ponctuelle : une seule date (start_date)
 *   - Hebdomadaire : jours de la semaine sélectionnés, tous les N semaines
 *   - Mensuelle : jours du mois sélectionnés, tous les N mois
 * Bornes : start_date obligatoire, end_date optionnel (ouvert)
 */

const WEEKDAYS = [
  { v: 0, l: "Lun" },
  { v: 1, l: "Mar" },
  { v: 2, l: "Mer" },
  { v: 3, l: "Jeu" },
  { v: 4, l: "Ven" },
  { v: 5, l: "Sam" },
  { v: 6, l: "Dim" },
];

const PRESET_COLORS = [
  "#F59E0B", // amber
  "#22C55E", // green
  "#3B82F6", // blue
  "#EC4899", // pink
  "#8B5CF6", // violet
  "#EF4444", // red
  "#14B8A6", // teal
  "#64748B", // slate
];

const EMPTY = {
  brand_id: "",
  title: "",
  color: "#F59E0B",
  description: "",
  category_id: "",
  start_time: "09:00",
  end_time: "12:00",
  recurrence_type: "once",
  recurrence_days: [],
  recurrence_interval: 1,
  start_date: "",
  end_date: "",
};

export default function AgendaSlotDialog({
  open,
  onClose,
  editing,          // slot object en mode édition, null en création
  defaultBrandId,
  defaultDate,      // ISO YYYY-MM-DD pour préremplir start_date en création
  brands,
  categories,       // shift_categories
  onSaved,          // (slot) => void — appelé après création/mise à jour
  onDeleted,        // (slot_id) => void — appelé après suppression
}) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        brand_id: editing.brand_id || "",
        title: editing.title || "",
        color: editing.color || "#F59E0B",
        description: editing.description || "",
        category_id: editing.category_id || "",
        start_time: editing.start_time || "09:00",
        end_time: editing.end_time || "12:00",
        recurrence_type: editing.recurrence_type || "once",
        recurrence_days: editing.recurrence_days || [],
        recurrence_interval: editing.recurrence_interval || 1,
        start_date: editing.start_date || defaultDate || "",
        end_date: editing.end_date || "",
      });
    } else {
      setForm({
        ...EMPTY,
        brand_id: defaultBrandId || brands?.[0]?.id || "",
        start_date: defaultDate || new Date().toISOString().slice(0, 10),
      });
    }
  }, [open, editing, defaultBrandId, defaultDate, brands]);

  const toggleDay = (v) => {
    setForm((f) => {
      const has = f.recurrence_days.includes(v);
      const next = has
        ? f.recurrence_days.filter((d) => d !== v)
        : [...f.recurrence_days, v].sort((a, b) => a - b);
      return { ...f, recurrence_days: next };
    });
  };

  const validate = () => {
    if (!form.brand_id) return "Département requis";
    if (!form.title.trim()) return "Intitulé requis";
    if (!form.start_date) return "Date de début requise";
    if (form.start_time >= form.end_time) return "L'heure de fin doit être après l'heure de début";
    if (form.recurrence_type === "weekly" && form.recurrence_days.length === 0)
      return "Sélectionne au moins un jour de la semaine";
    if (form.recurrence_type === "monthly" && form.recurrence_days.length === 0)
      return "Sélectionne au moins un jour du mois";
    if (form.end_date && form.end_date < form.start_date)
      return "La date de fin doit être postérieure à la date de début";
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        description: form.description || "",
        category_id: form.category_id || null,
        end_date: form.end_date || null,
      };
      const res = editing
        ? await api.put(`/agenda-slots/${editing.id}`, payload)
        : await api.post("/agenda-slots", payload);
      toast.success(editing ? "Plage mise à jour" : "Plage créée");
      onSaved?.(res.data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm("Supprimer définitivement cette plage horaire ?")) return;
    try {
      await api.delete(`/agenda-slots/${editing.id}`);
      toast.success("Plage supprimée");
      onDeleted?.(editing.id);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de suppression");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="agenda-slot-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {editing ? <Repeat size={18} /> : <CalendarPlus size={18} />}
            {editing ? "Modifier la plage" : "Nouvelle plage horaire réservée"}
          </DialogTitle>
          <DialogDescription>
            Une plage n&apos;est pas une intervention : c&apos;est un repère visuel pour le
            secrétariat (ex. « Mardi 8h-12h — RDV Consulting »). Aucun contrôle de
            chevauchement n&apos;est appliqué.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-uppercase mb-1.5 block">Département</Label>
              <SearchableSelect
                value={form.brand_id}
                onChange={(v) => setForm({ ...form, brand_id: v })}
                options={(brands || []).map((b) => ({
                  value: b.id,
                  label: b.name,
                  keywords: b.name,
                }))}
                placeholder="Sélectionner un département…"
                searchPlaceholder="Nom du département…"
                emptyLabel="Aucun département."
                data-testid="slot-brand"
              />
            </div>
            <div>
              <Label className="label-uppercase mb-1.5 block">Catégorie (optionnelle)</Label>
              <SearchableSelect
                value={form.category_id}
                onChange={(v) => setForm({ ...form, category_id: v })}
                options={(categories || []).map((c) => ({
                  value: c.id,
                  label: c.name,
                  keywords: c.name,
                }))}
                placeholder="Aucune"
                searchPlaceholder="Rechercher…"
                emptyLabel="Aucune catégorie."
                allowClear
                data-testid="slot-category"
              />
            </div>
          </div>

          <div>
            <Label className="label-uppercase mb-1.5 block">Intitulé</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ex. RDV Consulting, Astreinte, Formation…"
              data-testid="slot-title"
              maxLength={120}
            />
          </div>

          <div>
            <Label className="label-uppercase mb-1.5 block">Description (optionnelle)</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Contexte, consignes pour le secrétariat…"
              rows={2}
              data-testid="slot-description"
            />
          </div>

          <div>
            <Label className="label-uppercase mb-1.5 block">Couleur</Label>
            <div className="flex items-center gap-2 flex-wrap" data-testid="slot-colors">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm({ ...form, color: c })}
                  aria-label={`Couleur ${c}`}
                  data-testid={`slot-color-${c.replace("#", "").toLowerCase()}`}
                  className={`h-7 w-7 rounded-full border-2 transition-transform ${form.color === c ? "border-zinc-900 scale-110" : "border-zinc-200"}`}
                  style={{ backgroundColor: c }}
                />
              ))}
              <Input
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="w-12 h-8 p-1 cursor-pointer"
                data-testid="slot-color-custom"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-uppercase mb-1.5 block">Heure de début</Label>
              <TimePicker24
                value={form.start_time}
                onChange={(v) => setForm({ ...form, start_time: v })}
                data-testid="slot-start-time"
              />
            </div>
            <div>
              <Label className="label-uppercase mb-1.5 block">Heure de fin</Label>
              <TimePicker24
                value={form.end_time}
                onChange={(v) => setForm({ ...form, end_time: v })}
                data-testid="slot-end-time"
              />
            </div>
          </div>

          <div className="border rounded-sm border-zinc-200 p-3 space-y-3 bg-zinc-50/40">
            <div>
              <Label className="label-uppercase mb-1.5 block">Récurrence</Label>
              <Select
                value={form.recurrence_type}
                onValueChange={(v) => setForm({ ...form, recurrence_type: v, recurrence_days: [] })}
              >
                <SelectTrigger data-testid="slot-recurrence-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">Ponctuelle (une seule date)</SelectItem>
                  <SelectItem value="weekly">Hebdomadaire</SelectItem>
                  <SelectItem value="monthly">Mensuelle</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.recurrence_type === "weekly" && (
              <div>
                <Label className="label-uppercase mb-1.5 block">Jours de la semaine</Label>
                <div className="flex gap-1.5 flex-wrap" data-testid="slot-weekdays">
                  {WEEKDAYS.map((d) => {
                    const active = form.recurrence_days.includes(d.v);
                    return (
                      <button
                        key={d.v}
                        type="button"
                        onClick={() => toggleDay(d.v)}
                        data-testid={`slot-weekday-${d.v}`}
                        className={`px-2.5 py-1 text-xs font-medium rounded-sm border transition-colors ${
                          active
                            ? "bg-[#7C3AED] border-[#7C3AED] text-white"
                            : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400"
                        }`}
                      >{d.l}</button>
                    );
                  })}
                </div>
              </div>
            )}

            {form.recurrence_type === "monthly" && (
              <div>
                <Label className="label-uppercase mb-1.5 block">Jours du mois (1 → 31)</Label>
                <div className="grid grid-cols-10 gap-1" data-testid="slot-monthdays">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
                    const active = form.recurrence_days.includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => toggleDay(d)}
                        data-testid={`slot-monthday-${d}`}
                        className={`text-[11px] font-mono py-1 rounded-sm border transition-colors ${
                          active
                            ? "bg-[#7C3AED] border-[#7C3AED] text-white"
                            : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400"
                        }`}
                      >{d}</button>
                    );
                  })}
                </div>
              </div>
            )}

            {form.recurrence_type !== "once" && (
              <div>
                <Label className="label-uppercase mb-1.5 block">
                  {form.recurrence_type === "weekly" ? "Toutes les N semaines" : "Tous les N mois"}
                </Label>
                <Input
                  type="number"
                  min={1}
                  value={form.recurrence_interval}
                  onChange={(e) => setForm({ ...form, recurrence_interval: Math.max(1, parseInt(e.target.value || "1", 10)) })}
                  className="w-24"
                  data-testid="slot-recurrence-interval"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-uppercase mb-1.5 block">Date de début</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="slot-start-date"
                />
              </div>
              {form.recurrence_type !== "once" && (
                <div>
                  <Label className="label-uppercase mb-1.5 block">Date de fin (optionnelle)</Label>
                  <Input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    data-testid="slot-end-date"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          {editing && (
            <Button
              variant="outline"
              onClick={remove}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid="slot-delete-btn"
            >
              <Trash2 size={14} className="mr-1" /> Supprimer
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose} data-testid="slot-cancel-btn">
            Annuler
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
            data-testid="slot-save-btn"
          >
            {saving ? "Enregistrement…" : editing ? "Mettre à jour" : "Créer la plage"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
